import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Drawer, Box, Text, Badge, Group, Stack, Textarea, ActionIcon } from "@mantine/core";
import swal from "sweetalert";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { fmtRange, hrsBetween, initials, STATUS_BADGE, CAT_COLOR } from "./scheduleUtils";
import schedulerService from "../../../services/scheduler";
import { decrypt } from "../../../utils";
import { RequestTimeOffForm } from "./RequestTimeOffDrawer";
import "./SchedulerCreate.css";

const photoOf = (v) => (typeof v === "string" && v.startsWith("U2FsdGVk") ? decrypt(v) : v || null);

// Styled dropdown matching the rest of the Scheduler (cdd chrome) — same look
// as the Create Shift drawer's selects, not a native <select>.
function SelectDD({ placeholder, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const sel = options.find((o) => o.value === value);
  return (
    <div className={`cdd ${open ? "open" : ""}`} ref={ref}>
      <button type="button" className="cdd-btn" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        {sel ? <span>{sel.label}</span> : <span className="cdd-ph">{placeholder}</span>}
        <Icon name="chevronDown" size={16} />
      </button>
      <div className="cdd-menu">
        {options.length === 0 ? (
          <div className="cdd-opt" style={{ color: "var(--app-text-muted)", cursor: "default" }}><div className="co-name">No staff available</div></div>
        ) : options.map((o) => (
          <div key={o.value || "__empty"} className={`cdd-opt ${o.value === value ? "sel" : ""}`} onClick={() => { onChange(o.value); setOpen(false); }}>
            <div className="co-name">{o.label}</div>
            <span className="co-chk"><Icon name="check" size={15} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS_MEANING = {
  scheduled: "Planned. Awaiting the shift start and clock-in.",
  inprogress: "Caregiver is clocked in and on the visit now.",
  completed: "Shift finished. Hours are ready for review.",
  exception: "Needs attention. Something was flagged on this shift.",
  open: "Unassigned. Available for a staff member to pick up.",
  absent: "No-show. The caregiver did not clock in.",
};
const EXC_LABEL = {
  "no-show": "No-show. Caregiver did not clock in.",
  late: "Late clock-in.",
  "missed-evv": "Missing clock-out. EVV incomplete.",
  callout: "Call-out. Caregiver is unavailable.",
};

const Field = ({ label, icon, value, full }) => (
  <div className={`${css.dfield} ${full ? css.full : ""}`} style={full ? { gridColumn: "1 / -1" } : {}}>
    <div className={css.lbl}>{label}</div>
    <div className={css.val}>
      {icon ? <Icon name={icon} size={15} /> : null}
      <span>{value}</span>
    </div>
  </div>
);

export default function ShiftDrawer({ opened, shift, onClose, onChanged, onEdit, readOnly = false, staffOptions = [], onTimeOffSubmitted }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignTo, setAssignTo] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [localNotes, setLocalNotes] = useState(shift?.notes || []);
  // When true, this same drawer shows the Request Time Off form in place of the
  // shift detail (staff can request off for a shift's day without a 2nd panel).
  const [requesting, setRequesting] = useState(false);
  const prevShiftId = useRef(null);
  useEffect(() => {
    const id = shift?._id || shift?.id;
    if (id !== prevShiftId.current) {
      prevShiftId.current = id;
      setLocalNotes(shift?.notes || []);
      setRequesting(false);
    }
  }, [shift]);
  // Reset back to the detail view whenever the drawer is closed.
  useEffect(() => { if (!opened) setRequesting(false); }, [opened]);
  const { profile } = useSelector((state) => state.profile);
  const currentUser = profile?.user;
  // Only the account owner or a staff-admin may touch notes at all. The
  // account owner can manage any note; a staff-admin only their own.
  const isAccountOwner = currentUser?.type?.toLowerCase() !== "staff";
  const isStaffAdmin = isAccountOwner || currentUser?.role?.toLowerCase() === "admin";
  if (!shift) return null;

  const appt = shift.kind === "appointment";
  const isFac = shift.type === "facility";
  const dur = hrsBetween(shift.startMin, shift.endMin);
  const badge = STATUS_BADGE[shift.status] || STATUS_BADGE.scheduled;
  const dateLong = new Date(shift.date).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  const dateShort = new Date(shift.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const timeStr = shift.allDay ? "All day" : fmtRange(shift.startMin, shift.endMin);
  const typeLabel = isFac ? (appt ? "Facility Appointment" : "Facility Schedule") : appt ? "Client Appointment" : "Client Visit";

  const staffName = shift.staff?.name || "";
  const staffPhoto = photoOf(shift.staff?.photo);
  // Role comes from the staff record (staff.position = Job Title), the single
  // source of truth — not from the shift.
  const rawRole = shift.staff?.position || "";
  const staffRole = rawRole && rawRole.toLowerCase() !== "staff" ? rawRole : "Assigned staff";

  // Multi-client shifts: backend joins every client's name into shift.clientName.
  const multiClients = (shift.clients?.length || 0) > 1;
  const clientName = multiClients
    ? shift.clientName || shift.clients.map((c) => c.name).filter(Boolean).join(", ")
    : shift.client?.form1
    ? `${shift.client.form1.firstName || ""} ${shift.client.form1.lastName || ""}`.trim() || shift.client?.name || "Client"
    : shift.clientName || shift.client?.name || "Not set";
  const clientAddr = shift.client?.form1
    ? [shift.client.form1.currentAddress || shift.client.form1.address, shift.client.form1.city, shift.client.form1.state].filter(Boolean).join(", ")
    : "";
  const facName = shift.facility?.name || "Not set";
  const facAddr = [shift.facility?.address, shift.facility?.city, shift.facility?.state].filter(Boolean).join(", ");

  // Banner colour matches the calendar: appointments green, shifts (client or
  // facility) purple, open amber.
  const cat = shift.status === "open" ? "open" : appt ? "facility" : "client";
  const catColor = CAT_COLOR[cat] || CAT_COLOR.client;
  const headName = appt ? shift.apptTitle || typeLabel : staffName || "Unassigned";
  const headSub = appt ? typeLabel : staffName ? staffRole || "Assigned staff" : "No staff assigned";

  // open-shift (claim) values
  const isOpen = shift.status === "open";
  const openTypeLabel = isFac ? "Facility shift" : "Client visit";
  const openReason = shift.open?.reason || "Needs coverage";
  const openRole = shift.open?.role || (rawRole && rawRole.toLowerCase() !== "staff" ? rawRole : "") || "Any qualified staff";
  const openUrgent = shift.open?.urgent;
  const locName = isFac ? facName : clientName;

  // recurring series
  const recurring = shift.recurrence?.freq === "weekly";
  const recurUntil = shift.recurrence?.until
    ? new Date(shift.recurrence.until).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  // Actual attendance, populated on the shift as `trackingId` (a UserTracking doc).
  const tracking = shift.trackingId && typeof shift.trackingId === "object" ? shift.trackingId : null;
  const actualClockIn = tracking?.evvClockIn?.timestamp
    ? new Date(tracking.evvClockIn.timestamp)
    : tracking?.inout?.[0]?.start
    ? new Date(tracking.inout[0].start)
    : null;
  const actualClockOut = tracking?.evvClockOut?.timestamp
    ? new Date(tracking.evvClockOut.timestamp)
    : tracking?.inout?.[tracking.inout.length - 1]?.end
    ? new Date(tracking.inout[tracking.inout.length - 1].end)
    : null;

  const actionDone = shift.status === "completed" || shift.status === "inprogress";
  const actionItems = [
    { name: "Progress Notes", sub: "Visit summary and observations" },
    { name: "ADL", sub: "Activities of daily living" },
    { name: "MAR", sub: "Medication administration record" },
  ];

  const assign = async () => {
    if (!assignTo) return;
    setBusy(true);
    const done = () => { setAssignTo(null); onClose?.(); onChanged?.(); };
    if (shift.virtual) {
      // Schedule-generated open shift: materialize a real shift for the slot,
      // assigned to the chosen staff, on the slot's day.
      const dateStr = shift.date ? new Date(shift.date).toISOString().slice(0, 10) : null;
      const facId = shift.facility && (shift.facility._id || shift.facility.id || shift.facility);
      await schedulerService.createShift(
        {
          type: shift.type || "facility",
          kind: "shift",
          facility: facId || null,
          staff: assignTo,
          date: dateStr ? `${dateStr}T12:00:00` : undefined,
          startMin: shift.startMin,
          endMin: shift.endMin,
          status: "scheduled",
          recurrence: { freq: "none" },
        },
        done
      );
      setBusy(false);
      return;
    }
    await schedulerService.updateShift(shift._id || shift.id, { staff: assignTo, status: "scheduled" }, done);
    setBusy(false);
  };

  // Excuse an attendance discrepancy (late clock-in / missed shift). Persists
  // lateExcused so it stops being flagged as a discrepancy on the calendar,
  // KPI, and here (the no-show state is derived at read time and honours this).
  const excuse = async () => {
    setBusy(true);
    await schedulerService.updateShift(shift._id || shift.id, { lateExcused: true }, () => {
      // Close so the refreshed calendar reflects the excused state — the open
      // drawer is driven by an immutable shift prop and won't update in place.
      onClose?.();
      onChanged?.();
    });
    setBusy(false);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    await schedulerService.addShiftNote(shift._id || shift.id, note.trim(), (updated) => {
      setLocalNotes(updated?.notes || localNotes);
      setNote("");
      onChanged?.();
    });
    setBusy(false);
  };

  const canEditNote = (n) =>
    isStaffAdmin && (isAccountOwner || n.by === currentUser?.id || n.by === currentUser?._id);

  const startEditNote = (n) => {
    setEditingNoteId(n._id);
    setEditingText(n.text);
  };

  const saveEditNote = async () => {
    if (!editingText.trim()) return;
    setBusy(true);
    await schedulerService.updateShiftNote(shift._id || shift.id, editingNoteId, editingText.trim(), (updated) => {
      setLocalNotes(updated?.notes || localNotes);
      setEditingNoteId(null);
      setEditingText("");
      onChanged?.();
    });
    setBusy(false);
  };

  const deleteNote = async (n) => {
    const ok = await swal({
      title: "Delete note?",
      text: "This note will be permanently removed.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!ok) return;
    setBusy(true);
    await schedulerService.deleteShiftNote(shift._id || shift.id, n._id, (updated) => {
      setLocalNotes(updated?.notes || localNotes.filter((x) => x._id !== n._id));
      onChanged?.();
    });
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    await schedulerService.deleteShift(shift._id || shift.id, "single", () => {
      onClose?.();
      onChanged?.();
    });
    setBusy(false);
  };

  // Stop a weekly recurrence: removes this occurrence and all future ones,
  // keeping past shifts intact.
  const stopRecurrence = async () => {
    const ok = await swal({
      title: "Stop this recurrence?",
      text: "This shift and all future occurrences will be removed. Past shifts are kept.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Keep", "Stop recurrence"],
    });
    if (!ok) return;
    setBusy(true);
    await schedulerService.deleteShift(shift._id || shift.id, "future", () => {
      onClose?.();
      onChanged?.();
    });
    setBusy(false);
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={480}
      padding={0}
      withCloseButton={false}
      title={null}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      overlayProps={{ opacity: 0.5, blur: 3 }}
      zIndex={1000}
    >
      {requesting ? (
        <RequestTimeOffForm
          active={requesting}
          prefillStart={shift.date ? new Date(shift.date).toISOString().slice(0, 10) : ""}
          onBack={() => setRequesting(false)}
          onClose={onClose}
          onSubmitted={() => { setRequesting(false); onTimeOffSubmitted?.(); }}
        />
      ) : (
      <div className={css.dwWrap}>
        <div className={css.dwHead}>
          <div>
            <h3 className={css.dwTitle}>{isOpen ? "Open Shift" : appt ? shift.apptTitle || typeLabel : typeLabel}</h3>
            <div className={css.dwSub}>
              {isOpen ? `${dateLong} · ${timeStr}` : `${typeLabel} · ${dateLong} · ${timeStr}`}
            </div>
          </div>
          <button type="button" className={css.dwClose} onClick={onClose} aria-label="Close">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className={css.dwBody}>
          {isOpen ? (
            <>
              <div className={css.dBanner} style={{ background: "var(--app-tint-amber)", border: "1px solid var(--app-border)" }}>
                <span className={css.bAv} style={{ background: "var(--app-surface)", color: "var(--app-ink-amber)" }}><Icon name="calendar" size={18} /></span>
                <div style={{ flex: 1 }}>
                  <div className={css.bNm}>{openTypeLabel}</div>
                  <div className={css.bSub}>{openReason}</div>
                </div>
                <Badge color={openUrgent ? "red" : "yellow"} variant="light">{openUrgent ? "Urgent" : "Open"}</Badge>
              </div>

              <section className={css.dwSection}>
                <h5 className={css.dwSectionH}>Details</h5>
                <div className={css.dgrid}>
                  <Field label="Date" icon="calendar" value={dateShort} />
                  <Field label="Time" icon="clock" value={timeStr} />
                  <Field label={isFac ? "Facility" : "Client"} icon={isFac ? "hospital" : "user"} value={locName} />
                  <Field label="Role" icon="user" value={openRole} />
                </div>
              </section>

              {!readOnly && (
                <section className={css.dwSection}>
                  <h5 className={css.dwSectionH}>Assign to qualified staff</h5>
                  <div className="sd-create">
                    <SelectDD
                      placeholder="Select staff member…"
                      value={assignTo || ""}
                      options={staffOptions}
                      onChange={(v) => setAssignTo(v || null)}
                    />
                  </div>
                </section>
              )}
            </>
          ) : (
            <>
          {shift.exc && !shift.lateExcused ? (
            <div className={`${css.dBanner} ${css.exc}`} style={{ marginBottom: 14 }}>
              <span className={css.bAv} style={{ background: "var(--app-surface)" }}><Icon name="alert" size={20} /></span>
              <div style={{ flex: 1 }}>
                <div className={css.bNm} style={{ fontSize: 14 }}>{EXC_LABEL[shift.exc] || "Exception"}</div>
                <div className={css.bSub}>Flagged automatically</div>
              </div>
              {!readOnly ? (
                <button type="button" className={`${css.btnXs} ${css.btnXsGhost}`} onClick={excuse} disabled={busy}>
                  Excuse
                </button>
              ) : null}
            </div>
          ) : shift.lateExcused ? (
            <div className={css.dBanner} style={{ marginBottom: 14, background: "var(--app-surface)", border: "1px solid var(--app-border)" }}>
              <span className={css.bAv} style={{ background: "var(--app-surface)" }}><Icon name="check" size={20} /></span>
              <div style={{ flex: 1 }}>
                <div className={css.bNm} style={{ fontSize: 14 }}>Excused</div>
                <div className={css.bSub}>{shift.exc ? `${EXC_LABEL[shift.exc]} · excused by admin` : "Attendance discrepancy excused by admin"}</div>
              </div>
            </div>
          ) : null}

          {/* primary banner — coloured to match the calendar item's category */}
          <div className={css.dBanner} style={{ background: catColor.soft, border: `1px solid ${catColor.line}` }}>
            <span className={css.bAv} style={{ color: catColor.ink }}>
              {appt && !staffName ? (
                <Icon name="calendar" size={18} />
              ) : staffPhoto ? (
                <img src={staffPhoto} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : staffName ? (
                initials(staffName)
              ) : (
                <Icon name="user" size={18} />
              )}
            </span>
            <div style={{ flex: 1 }}>
              <div className={css.bNm}>{headName}</div>
              <div className={css.bSub}>{headSub}</div>
            </div>
            <Badge color={badge.color} variant="light">{badge.label}</Badge>
          </div>

          <section className={css.dwSection}>
            <h5 className={css.dwSectionH}>Schedule</h5>
            <div className={css.dgrid}>
              <Field label="Type" icon={isFac ? "hospital" : "user"} value={typeLabel} />
              <Field label="Date" icon="calendar" value={dateShort} />
              <Field label="Time" icon="clock" value={timeStr} />
              <Field label="Scheduled hours" icon="timer" value={shift.allDay ? "All day" : `${dur.toFixed(1)}h`} />
              {recurring ? (
                <Field full label="Repeats" icon="repeat" value={recurUntil ? `Repeats weekly until ${recurUntil}` : "Repeats weekly"} />
              ) : null}
              <Field full label="Status" value={<span style={{ fontWeight: 500, color: "var(--app-text-muted)", fontSize: 12.5 }}>{STATUS_MEANING[shift.status] || ""}</span>} />
            </div>
          </section>

          {!isFac && (
            <section className={css.dwSection}>
              <h5 className={css.dwSectionH}>Client</h5>
              <div className={css.dgrid}>
                <Field
                  full
                  label="Name"
                  icon="user"
                  value={
                    multiClients ? (
                      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {shift.clients.map((c) => (
                          <span key={c._id || c.name}>{c.name}</span>
                        ))}
                      </span>
                    ) : (
                      clientName
                    )
                  }
                />
                <Field full label="Address" icon="pin" value={clientAddr || "Not set"} />
                {shift.service ? <Field full label="Service" icon="heart" value={shift.service} /> : null}
              </div>
            </section>
          )}

          <section className={css.dwSection}>
            <h5 className={css.dwSectionH}>Facility</h5>
            <div className={css.dgrid}>
              <Field full label="Name" icon="hospital" value={facName} />
              <Field full label="Address" icon="pin" value={facAddr || "Not set"} />
            </div>
          </section>

          {appt && (
            <section className={css.dwSection}>
              <h5 className={css.dwSectionH}>Staff</h5>
              {staffName ? (
                <div className={css.dBanner} style={{ margin: 0, background: catColor.soft, border: `1px solid ${catColor.line}` }}>
                  <span className={css.bAv} style={{ color: catColor.ink }}>
                    {staffPhoto ? <img src={staffPhoto} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : initials(staffName)}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div className={css.bNm}>{staffName}</div>
                    <div className={css.bSub}>{staffRole || "Assigned staff"}</div>
                  </div>
                </div>
              ) : (
                <div className={css.excClear}><Icon name="user" size={15} /> No staff assigned</div>
              )}
            </section>
          )}

          {!appt && (
            <section className={css.dwSection}>
              <h5 className={css.dwSectionH}>EVV &amp; Time Tracking</h5>
              {tracking ? (
                <div className={css.dgrid}>
                  <Field label="Scheduled" icon="clock" value={timeStr} />
                  <Field
                    label="Actual clock-in"
                    icon="clock"
                    value={actualClockIn ? actualClockIn.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "Not yet clocked in"}
                  />
                  <Field
                    label="Actual clock-out"
                    icon="clock"
                    value={actualClockOut ? actualClockOut.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "Not yet clocked out"}
                  />
                </div>
              ) : (
                <div className={css.excClear}>
                  <Icon name="clock" size={15} /> No clock-in recorded yet · EVV check-in required at the visit.
                </div>
              )}
            </section>
          )}

          {!appt && (
            <section className={css.dwSection}>
              <h5 className={css.dwSectionH}>Action Taken</h5>
              {actionItems.map((it) => (
                <div key={it.name} className={`${css.actionRow} ${actionDone ? css.done : css.pending}`}>
                  <span className={css.aiIc}><Icon name={actionDone ? "check" : "clock"} size={13} /></span>
                  <span className={css.aiBody}>
                    <span className={css.aiName}>{it.name}</span>
                    <span className={css.aiSub}>{it.sub}</span>
                  </span>
                  <span className={css.aiStatus}>{actionDone ? "Logged" : "Pending"}</span>
                </div>
              ))}
            </section>
          )}

          <section className={css.dwSection}>
            <h5 className={css.dwSectionH}>Notes</h5>
            <Stack spacing={8}>
              {(localNotes || []).map((n, i) => (
                <Box key={n._id || i} style={{ background: "var(--app-surface-2)", border: "1px solid var(--app-border)", borderRadius: 8, padding: "8px 10px" }}>
                  {editingNoteId === n._id ? (
                    <>
                      <Textarea value={editingText} onChange={(e) => setEditingText(e.currentTarget.value)} autosize minRows={2} />
                      <Group position="right" mt={6} spacing={8}>
                        <button type="button" className={css.btnG} style={{ height: 30, fontSize: 12.5 }} onClick={() => setEditingNoteId(null)}>
                          Cancel
                        </button>
                        <button type="button" className={css.btnP} style={{ height: 30, fontSize: 12.5 }} onClick={saveEditNote} disabled={!editingText.trim() || busy}>
                          Save
                        </button>
                      </Group>
                    </>
                  ) : (
                    <Group position="apart" align="flex-start" noWrap>
                      <Box style={{ flex: 1 }}>
                        <Text fz={13} c="var(--app-text)">{n.text}</Text>
                        <Text fz={11} c="var(--app-text-muted)" mt={2}>{n.at ? new Date(n.at).toLocaleString() : ""}</Text>
                      </Box>
                      {!readOnly && canEditNote(n) && (
                        <Group spacing={4} noWrap>
                          <ActionIcon size="sm" variant="subtle" onClick={() => startEditNote(n)}>
                            <Icon name="edit" size={14} />
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" color="red" onClick={() => deleteNote(n)}>
                            <Icon name="trash" size={14} />
                          </ActionIcon>
                        </Group>
                      )}
                    </Group>
                  )}
                </Box>
              ))}
              {!(localNotes || []).length && <Text fz={12.5} c="var(--app-text-muted)">No notes yet.</Text>}
              {!readOnly && (
                <>
                  <Textarea placeholder="Add a note for this shift..." value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} />
                  <Group position="right">
                    <button type="button" className={css.btnG} style={{ height: 34, fontSize: 13 }} onClick={addNote} disabled={!note.trim() || busy}>
                      Add note
                    </button>
                  </Group>
                </>
              )}
            </Stack>
          </section>
            </>
          )}
        </div>

        <div className={css.dwFoot}>
          {isOpen ? (
            <>
              <button type="button" className={css.btnG} onClick={onClose}>Close</button>
              <span style={{ flex: 1 }} />
              <button type="button" className={css.btnP} onClick={assign} disabled={!assignTo || busy}>
                <Icon name="check" size={15} /> Assign to staff
              </button>
            </>
          ) : (
            <>
              {!readOnly && (
                <button type="button" className={css.btnG} style={{ color: "var(--app-ink-red)", borderColor: "var(--app-border)" }} onClick={remove} disabled={busy}>
                  {appt ? "Cancel appointment" : "Cancel shift"}
                </button>
              )}
              {!readOnly && recurring && (
                <button type="button" className={css.btnG} style={{ color: "var(--app-ink-amber)", borderColor: "var(--app-border)" }} onClick={stopRecurrence} disabled={busy}>
                  Stop recurrence
                </button>
              )}
              <span style={{ flex: 1 }} />
              {readOnly && onTimeOffSubmitted && (
                <button type="button" className={css.btnP} onClick={() => setRequesting(true)}>
                  <Icon name="plus" size={15} /> Request Time Off
                </button>
              )}
              {!readOnly && onEdit && (
                <button type="button" className={css.btnP} onClick={() => onEdit(shift)} disabled={busy}>
                  <Icon name="edit" size={15} /> Edit {appt ? "appointment" : "shift"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      )}
    </Drawer>
  );
}
